import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BadgeCheck,
  Check,
  ChevronDown,
  Copy,
  FileText,
  Loader2,
  Pin,
  RefreshCw,
  Scale,
  ThumbsDown,
  ThumbsUp,
  Upload,
} from 'lucide-react';
import { Card, Tag } from '@/components/ui';
import { cn } from '@/lib/cn';
import {
  clearResponseFeedback,
  setResponseFeedback,
  type FeedbackRating,
} from '@/lib/api-client';
import type { ResponseAuditTrail, ResponseRecord } from '@/lib/types';

// Three render states for one card slot in the response feed:
//   loading  -> Claude call in flight, show the prompt label + spinner
//   error    -> the call failed, show what went wrong + retry
//   response -> the saved Response row from the api
export type ResponseCardState =
  | { kind: 'loading'; label?: string | null }
  | {
      kind: 'streaming';
      label?: string | null;
      text: string;
      /** P2 Section 1: which mode produced this stream, for the card badge. */
      mode?: 'fast' | 'deep' | 'live';
      /** Rough live token estimate shown top-right while streaming. */
      tokens?: number;
    }
  | { kind: 'error'; label?: string | null; message: string; onRetry?: () => void }
  | { kind: 'response'; response: ResponseRecord };

interface ResponseCardProps {
  state: ResponseCardState;
  /** Active matter id, for the thin-matter upload link in the grounding footer. */
  matterId?: string | null;
}

export function ResponseCard({ state, matterId }: ResponseCardProps) {
  if (state.kind === 'loading') {
    return <LoadingCard label={state.label ?? null} />;
  }
  if (state.kind === 'streaming') {
    return (
      <StreamingCard
        label={state.label ?? null}
        text={state.text}
        mode={state.mode ?? 'deep'}
        tokens={state.tokens ?? 0}
      />
    );
  }
  if (state.kind === 'error') {
    return (
      <ErrorCard
        label={state.label ?? null}
        message={state.message}
        {...(state.onRetry !== undefined && { onRetry: state.onRetry })}
      />
    );
  }
  return <SuccessCard response={state.response} matterId={matterId ?? null} />;
}

// --- success -----------------------------------------------------------------

function SuccessCard({ response, matterId }: { response: ResponseRecord; matterId: string | null }) {
  const audit = response.auditTrail ?? null;
  return (
    <Card variant="default" padding="md" className="space-y-3">
      <CardHeader
        label={response.label ?? 'Custom prompt'}
        timestamp={response.createdAt}
        durationMs={response.durationMs}
        mode={audit?.mode}
      />
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-stone-200">
        {response.answer}
      </p>
      {/* P6 S2/S3: honest grounding footer — names the matter documents the answer
          stood on, or says transcript-only and offers a one-tap upload path. */}
      <GroundingFooter audit={audit} matterId={matterId} />
      <CardFooter response={response} />
      {/* P2 Section 2 addition 3: honest measured timings, never aspirational. */}
      <TimingFooter audit={audit} fallbackMs={response.durationMs} />
      {/* P2 Section 2 addition 1: full provenance, collapsible. */}
      {audit && hasAuditContent(audit) ? <AuditPanel audit={audit} /> : null}
    </Card>
  );
}

// P6 S2/S3: the grounding footer. Distinct matter documents the answer was
// grounded on (from the audit trail), or an honest transcript-only line with a
// one-tap upload path so a thin matter can never masquerade as evidence-based.
function groundingDocs(audit: ResponseAuditTrail | null): string[] {
  if (!audit) {
    return [];
  }
  if (audit.documents && audit.documents.length > 0) {
    return Array.from(new Set(audit.documents.map((d) => d.filename)));
  }
  if (audit.retrievedChunks && audit.retrievedChunks.length > 0) {
    return Array.from(new Set(audit.retrievedChunks.map((c) => c.filename)));
  }
  return [];
}

// Legal Brain A4: the verified authorities injected for this answer, rendered
// as distinct Authority chips so a statutory or case citation is visibly
// backed by ingested source text, not model memory.
function AuthorityChips({ audit }: { audit: ResponseAuditTrail | null }) {
  const authorities = audit?.authorities ?? [];
  if (authorities.length === 0) {
    return null;
  }
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-[11px] text-stone-500">
      <Scale className="h-3 w-3 shrink-0 text-sky-500" aria-hidden />
      <span>Authorities:</span>
      {authorities.map((a) => (
        <span
          key={a.id}
          title={`${a.title} — ${a.citation} (${a.jurisdiction})`}
          className="inline-flex items-center gap-1 rounded border border-sky-900/60 bg-sky-950/30 px-1.5 py-0.5 text-sky-300"
        >
          {a.pinned ? <Pin className="h-2.5 w-2.5" aria-hidden /> : null}
          {a.citation}
        </span>
      ))}
    </p>
  );
}

function GroundingFooter({
  audit,
  matterId,
}: {
  audit: ResponseAuditTrail | null;
  matterId: string | null;
}) {
  const docs = groundingDocs(audit);
  if (docs.length > 0) {
    const shown = docs.slice(0, 3).join(', ');
    const more = docs.length > 3 ? ` +${docs.length - 3} more` : '';
    return (
      <div className="space-y-1">
        <p className="flex items-start gap-1.5 text-[11px] text-stone-500">
          <FileText className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" aria-hidden />
          <span>
            Grounded on: {shown}
            {more} + live transcript
          </span>
        </p>
        <AuthorityChips audit={audit} />
      </div>
    );
  }
  if ((audit?.authorities?.length ?? 0) > 0) {
    return <AuthorityChips audit={audit} />;
  }
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-amber-400/90">
      <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
      <span className="text-stone-400">
        Transcript only — no matter documents loaded for this matter.
      </span>
      {matterId ? (
        <Link
          to={`/admin/matters/${matterId}/documents`}
          className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300"
        >
          <Upload className="h-3 w-3" aria-hidden />
          Upload documents
        </Link>
      ) : null}
    </p>
  );
}

function hasAuditContent(a: ResponseAuditTrail): boolean {
  // A pre-P2 row persisted `{}`; only show the panel when we actually captured a
  // trail (mode is always set by the P2 route).
  return a.mode !== undefined || a.usage !== undefined;
}

// Honest timing line: real measured first-token + total, token count, est. cost.
function TimingFooter({
  audit,
  fallbackMs,
}: {
  audit: ResponseAuditTrail | null;
  fallbackMs: number;
}) {
  const totalMs = audit?.durationMs ?? fallbackMs;
  const parts: string[] = [];
  if (audit?.firstTokenMs && audit.firstTokenMs > 0) {
    parts.push(`First token ${fmtSec(audit.firstTokenMs)}`);
  }
  parts.push(`total ${fmtSec(totalMs)}`);
  const out = audit?.usage?.outputTokens;
  if (typeof out === 'number') {
    parts.push(`${out} tokens`);
  }
  if (audit?.cost && audit.cost.usd >= 0) {
    parts.push(formatUsd(audit.cost.usd));
  }
  return (
    <p className="text-[10px] tabular-nums text-stone-600" title={audit?.cost?.rateNote ?? ''}>
      {parts.join(', ')}
    </p>
  );
}

function AuditPanel({ audit }: { audit: ResponseAuditTrail }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-stone-800/60 pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
        aria-expanded={open}
      >
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} aria-hidden />
        {open ? 'Hide audit' : 'Show audit'}
      </button>
      {open ? (
        <dl className="mt-2 space-y-1.5 text-[11px] leading-relaxed text-stone-400">
          <AuditRow label="Mode" value={audit.mode ? audit.mode.toUpperCase() : 'unknown'} />
          <AuditRow
            label="Legal skills"
            value={
              audit.legalSkills && audit.legalSkills.length > 0
                ? audit.legalSkills.map((s) => s.name).join(', ')
                : 'none'
            }
          />
          {audit.legalSkillsTrimmed && audit.legalSkillsTrimmed.length > 0 ? (
            <AuditRow
              label="Skills trimmed (budget)"
              value={audit.legalSkillsTrimmed.map((s) => s.name).join(', ')}
            />
          ) : null}
          <AuditRow
            label="Knowledge injected"
            value={
              audit.knowledgeEntries && audit.knowledgeEntries.length > 0
                ? audit.knowledgeEntries.map((k) => k.title).join('; ')
                : 'none'
            }
          />
          <AuditRow
            label="Documents referenced"
            value={
              audit.documents && audit.documents.length > 0
                ? audit.documents.map((d) => d.filename).join(', ')
                : 'none'
            }
          />
          <AuditRow
            label="MCP tools called"
            value={
              audit.mcpToolsCalled && audit.mcpToolsCalled.length > 0
                ? audit.mcpToolsCalled.map((t) => `${t.server}.${t.tool}`).join(', ')
                : 'none'
            }
          />
          <AuditRow
            label="Verified via AustLII"
            value={
              audit.citationsVerifiedViaWebSearch && audit.citationsVerifiedViaWebSearch.length > 0
                ? audit.citationsVerifiedViaWebSearch.join('; ')
                : audit.mode === 'fast'
                  ? `not live-checked (fast mode); verified pool size ${audit.verifiedPoolSize ?? 0}`
                  : 'no live search ran'
            }
          />
          <AuditRow
            label="Unverified flag"
            value={
              audit.flaggedUnverified
                ? 'the answer flagged something as unverified — confirm before relying'
                : 'none flagged'
            }
          />
          {audit.usage ? (
            <AuditRow
              label="Tokens"
              value={`in ${audit.usage.inputTokens}, out ${audit.usage.outputTokens}, cache write ${audit.usage.cacheCreationInputTokens}, cache read ${audit.usage.cacheReadInputTokens}`}
            />
          ) : null}
          {audit.cost ? (
            <AuditRow label="Est. cost" value={`${formatUsd(audit.cost.usd)} — ${audit.cost.rateNote}`} />
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}

function AuditRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-32 shrink-0 uppercase tracking-[0.15em] text-stone-600">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-stone-300">{value}</dd>
    </div>
  );
}

function fmtSec(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatUsd(usd: number): string {
  if (usd > 0 && usd < 0.01) {
    return '<$0.01';
  }
  return `$${usd.toFixed(2)}`;
}

// --- loading -----------------------------------------------------------------

function LoadingCard({ label }: { label: string | null }) {
  return (
    <Card variant="default" padding="md" className="space-y-3">
      <CardHeader label={label ?? 'Custom prompt'} timestamp={null} durationMs={null} />
      <div className="flex items-center gap-2 text-sm text-stone-400">
        <Loader2 className="h-4 w-4 animate-spin text-amber-500" aria-hidden />
        <span>Claude is thinking</span>
      </div>
    </Card>
  );
}

// --- streaming ---------------------------------------------------------------

// The live card while Claude writes. Header carries an amber pulsing "Live"
// dot; the body shows the accumulating text with a blinking caret. No sources
// or copy yet -- those land when the stream completes and the card becomes a
// SuccessCard. Amber + stone only, per the design language.
function StreamingCard({
  label,
  text,
  mode,
  tokens,
}: {
  label: string | null;
  text: string;
  mode: 'fast' | 'deep' | 'live';
  tokens: number;
}) {
  return (
    <Card variant="default" padding="md" className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="flex min-w-0 items-baseline gap-2 truncate text-xs font-medium uppercase tracking-[0.18em] text-amber-500">
          <span className="truncate">{formatLabel(label ?? 'Custom prompt')}</span>
          <ModeBadge mode={mode} />
        </p>
        <span className="flex shrink-0 items-center gap-2">
          {tokens > 0 ? (
            <span className="text-[10px] tabular-nums text-stone-600" aria-label="tokens so far">
              {tokens}
            </span>
          ) : null}
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-amber-400">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" aria-hidden />
            Live
          </span>
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-stone-200">
        {text}
        <span className="ml-0.5 inline-block animate-pulse font-medium text-amber-500" aria-hidden>
          ▋
        </span>
      </p>
    </Card>
  );
}

// --- error -------------------------------------------------------------------

function ErrorCard({
  label,
  message,
  onRetry,
}: {
  label: string | null;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <Card variant="default" padding="md" className="space-y-3 border-red-900/40 bg-red-950/10">
      <CardHeader
        label={label ?? 'Custom prompt'}
        timestamp={null}
        durationMs={null}
        tone="error"
      />
      <div className="flex items-start gap-2 text-sm text-red-300">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" aria-hidden />
        <p>{message}</p>
      </div>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-amber-400"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Try again
        </button>
      ) : null}
    </Card>
  );
}

// --- shared parts ------------------------------------------------------------

// P5.1: a prominent FAST (amber) / DEEP (stone) badge so the operator always
// knows which kind of answer a card is — fast tactical vs deep researched.
function ModeBadge({ mode }: { mode?: 'fast' | 'deep' | 'live' }) {
  if (mode === 'fast') {
    return (
      <span className="shrink-0 rounded border border-amber-700/50 bg-amber-950/30 px-1 py-0.5 text-[9px] font-medium tracking-[0.15em] text-amber-400">
        FAST
      </span>
    );
  }
  if (mode === 'deep') {
    return (
      <span className="shrink-0 rounded border border-stone-600 px-1 py-0.5 text-[9px] tracking-[0.15em] text-stone-400">
        DEEP
      </span>
    );
  }
  if (mode === 'live') {
    return (
      <span className="shrink-0 rounded border border-emerald-700/50 bg-emerald-950/30 px-1 py-0.5 text-[9px] font-medium tracking-[0.15em] text-emerald-400">
        LIVE
      </span>
    );
  }
  return null;
}

interface CardHeaderProps {
  label: string;
  timestamp: string | null;
  durationMs: number | null;
  tone?: 'default' | 'error';
  mode?: 'fast' | 'deep' | 'live' | undefined;
}

function CardHeader({ label, timestamp, durationMs, tone = 'default', mode }: CardHeaderProps) {
  const labelClass =
    tone === 'error'
      ? 'truncate text-xs font-medium uppercase tracking-[0.18em] text-red-400'
      : 'truncate text-xs font-medium uppercase tracking-[0.18em] text-amber-500';
  return (
    <div className="flex items-baseline justify-between gap-3">
      <p className="flex min-w-0 items-baseline gap-2">
        <span className={labelClass}>{formatLabel(label)}</span>
        <ModeBadge mode={mode} />
      </p>
      <p className="shrink-0 text-[10px] uppercase tracking-[0.18em] text-stone-600">
        {timestamp ? formatTimestamp(timestamp) : '...'}
        {durationMs !== null && timestamp ? ` · ${formatDuration(durationMs)}` : null}
      </p>
    </div>
  );
}

function CardFooter({ response }: { response: ResponseRecord }) {
  const tools = response.toolsUsed ?? [];
  const verified = tools.some((t) => t.tool === 'web_search' || t.server === 'web');
  // 'web' is surfaced by the dedicated AustLII chip, so drop it from the plain
  // source chips to avoid a redundant "web" tag.
  const sources = uniqueServers(tools).filter((server) => server !== 'web');
  return (
    <div className="flex items-center justify-between gap-3 pt-1">
      <SourcesRow sources={sources} verified={verified} />
      <div className="flex items-center gap-1">
        <FeedbackButtons response={response} />
        <CopyButton text={response.answer} />
      </div>
    </div>
  );
}

// Legal Brain B6: thumbs on a response card. One rating per response (a second
// tap on the same thumb clears it); DOWN patterns feed the weekly prompt
// improvement analysis.
function FeedbackButtons({ response }: { response: ResponseRecord }) {
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  async function toggle(next: FeedbackRating): Promise<void> {
    try {
      if (rating === next) {
        await clearResponseFeedback(response.sessionId, response.id);
        setRating(null);
      } else {
        await setResponseFeedback(response.sessionId, response.id, next);
        setRating(next);
      }
    } catch {
      // feedback is best-effort; never disturb the operator mid-hearing
    }
  }
  return (
    <>
      <button
        type="button"
        aria-label="Good response"
        aria-pressed={rating === 'UP'}
        onClick={() => void toggle('UP')}
        className={cn(
          'rounded p-1 transition-colors',
          rating === 'UP' ? 'text-emerald-400' : 'text-stone-600 hover:text-stone-400',
        )}
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label="Poor response"
        aria-pressed={rating === 'DOWN'}
        onClick={() => void toggle('DOWN')}
        className={cn(
          'rounded p-1 transition-colors',
          rating === 'DOWN' ? 'text-red-400' : 'text-stone-600 hover:text-stone-400',
        )}
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>
    </>
  );
}

function SourcesRow({ sources, verified }: { sources: string[]; verified: boolean }) {
  // P6 S2: no misleading "No tools called". External tool calls (MCP / AustLII)
  // are a DEEP-mode concept; matter-document grounding is shown by the grounding
  // footer above. When no external tools ran, render nothing here.
  if (sources.length === 0 && !verified) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Sources:</span>
      {sources.map((server) => (
        <Tag key={server} tone="muted">
          {server}
        </Tag>
      ))}
      {verified ? (
        <span className="inline-flex items-center gap-1 rounded border border-amber-900/40 bg-amber-950/20 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-amber-400">
          <BadgeCheck className="h-3 w-3" aria-hidden />
          Verified via AustLII
        </span>
      ) : null}
    </div>
  );
}

function CopyButton({ text }: { text: string }): ReactNode {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard write can fail on insecure origins or denied permission.
      // Silent failure here is fine; the copy button is a convenience.
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'inline-flex items-center gap-1.5 rounded text-xs transition-colors',
        copied ? 'text-emerald-400' : 'text-stone-500 hover:text-amber-400',
      )}
      aria-label="Copy response to clipboard"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" aria-hidden />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" aria-hidden />
          Copy
        </>
      )}
    </button>
  );
}

// --- formatting helpers ------------------------------------------------------

function formatLabel(label: string): string {
  // Quick-action ids come through as "counter-argument"; render as
  // "Counter argument" without leaking the slug.
  return label
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatTimestamp(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms} ms`;
  }
  return `${(ms / 1000).toFixed(1)} s`;
}

function uniqueServers(tools: ResponseRecord['toolsUsed']): string[] {
  const seen = new Set<string>();
  for (const tool of tools) {
    seen.add(tool.server);
  }
  return Array.from(seen).sort();
}
